import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, MailCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /auth/callback?token=…&state=…
 *
 * Landing page for the magic link emailed by the backend. Reads token+state
 * from the URL, POSTs to /api/auth/magic-link/consume. The backend sets the
 * httpOnly `tm_auth` cookie on the response (Phase B, cookie-only) — we just
 * refresh the auth context to pull /auth/me and redirect to admin/portal.
 *
 * The token is consumed by POST (not GET) so it never leaks into browser
 * history, server logs, or proxy logs once we drop the URL.
 */
export default function AuthCallback() {
    const [sp] = useSearchParams();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [phase, setPhase] = useState("working"); // working | done | failed
    const [errMsg, setErrMsg] = useState("");
    // Guard against StrictMode double-mount in dev burning the token on first paint.
    const consumedRef = useRef(false);

    useEffect(() => {
        if (consumedRef.current) return;
        const token = sp.get("token");
        const state = sp.get("state");
        const next = sp.get("next") || "";
        if (!token || !state) {
            setPhase("failed");
            setErrMsg("This link is missing required parts. Please request a new one from the sign-in page.");
            return;
        }
        consumedRef.current = true;
        (async () => {
            try {
                const { data } = await axios.post(`${API}/auth/magic-link/consume`, { token, state });
                // Backend sets the httpOnly cookie on this response; no client-side
                // token storage needed. /auth/me will pick the session up next.
                const me = await refresh();
                const user = me || data.user;
                toast.success(`Welcome back, ${user?.dentist_name || user?.name || user?.email || ""}`.trim());
                const role = user?.role || data.user?.role || "client";
                const dest = next || (role !== "client" ? "/admin" : "/portal");
                setPhase("done");
                nav(dest, { replace: true });
            } catch (e) {
                const detail = e?.response?.data?.detail || e.message || "Sign-in failed";
                setErrMsg(detail);
                setPhase("failed");
            }
        })();
    }, []);

    return (
        <div data-testid="auth-callback-page" className="min-h-[calc(100vh-68px)] flex items-center justify-center px-6 bg-slate-50">
            <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
                {phase === "working" && (
                    <>
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cyan-50 text-cyan-700">
                            <Loader2 size={24} className="animate-spin" />
                        </div>
                        <h1 className="mt-5 font-display text-2xl tracking-tight text-slate-900">Signing you in…</h1>
                        <p className="mt-2 text-[13.5px] text-slate-500">Verifying your one-time link.</p>
                    </>
                )}
                {phase === "done" && (
                    <>
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-700">
                            <MailCheck size={24} />
                        </div>
                        <h1 className="mt-5 font-display text-2xl tracking-tight text-slate-900">Signed in.</h1>
                        <p className="mt-2 text-[13.5px] text-slate-500">Redirecting…</p>
                    </>
                )}
                {phase === "failed" && (
                    <>
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-50 text-rose-700">
                            <AlertTriangle size={24} />
                        </div>
                        <h1 className="mt-5 font-display text-2xl tracking-tight text-slate-900">Link didn&rsquo;t work.</h1>
                        <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed" data-testid="auth-callback-error">
                            {errMsg}
                        </p>
                        <Link
                            to="/login"
                            data-testid="auth-callback-retry"
                            className="mt-6 inline-flex items-center gap-2 tr-btn !py-2.5 text-[13px]"
                        >
                            Request a new sign-in link <ArrowRight size={14} />
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
